// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import CustomBreadcrumbs from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import { PAGE_SIZE } from "../../services/common/const";
import { GetForm3cForm25List } from "../../services/Form3cForm25Service";

import { format } from "date-fns";

import { PreferencesContext } from "../../PreferenceContext";
import { useNavigate, useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import {
  GetForm3cForm25Report,
  GetProcedureStatusReport,
} from "../../services/ReportService";
import { ExportFilesFunction } from "../../shared/CommonFunction";
import { bindMenu, usePopupState } from "material-ui-popup-state/hooks";
import { GetProcedureStatusList } from "../../services/ProcedureStatusReport";
import MultiSelect from "../../components/CustomFields/MultiSelect";
import { selectOption } from "../../shared/Model/Common";

// SCHEMA
interface Data {
  billingId: number;
  code: string;
  authorizePersion: string;
  organization: string;
  designation: string;
  email: string;
  website: string;
  plan: string;
  none: string;
}

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  //onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

// SCHEMA FOR RESULTDATA
interface ResultData {
  procedureReportColl: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
  totalReceivedAmount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  procedureReportColl: [],
  tabTypeColl: [],
  totalCount: 0,
  totalReceivedAmount: 0,
};

// SCHEMA FOR PARAMETER
interface Parameter {
  billingId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  startDate: Date;
  endDate: Date;
  procedureType: selectOption[] | null;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  billingId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  startDate: new Date(),
  endDate: new Date(),
  procedureType: null,
};

const procedureTypeOptions = [
  { value: "COMPLETED", label: "Completed", bg: "#c19603", type: "complete" },
  { value: "PLANNED", label: "Planned", bg: "#787d81", type: "planned" },
  {
    value: "INPROGRESS",
    label: "In progress",
    bg: "#448944",
    type: "in-progress",
  },
  // { value: "OVERDUE", label: "Overdue", bg: "#9f2d2d",type: "follow-up" },
];

const ProcedureStatusReport = () => {
  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");

  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [page_Index, setPage_Index] = useState(1);
  const [counData, setCounData] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);

  const navigate = useNavigate();

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let page = urlParams.get("page");
  let encodedQuery = urlParams.get("q");
  let procedureTypeParam = urlParams.get("type");
  // let fd = urlParams.get("fd");
  // let ed = urlParams.get("ed");

  let query = "";
  let curPage = 1;

  let startDate: any = null;
  let endDate: any = null;

  if (page != null && page !== "" && page !== undefined) {
    curPage = +page;
  }

  if (encodedQuery !== null && encodedQuery !== undefined) {
    query = encodedQuery;
  }

  const [value, setValue] = useState(query || "");
  const popupState = usePopupState({ variant: "popover", popupId: "demoMenu" });
  const [param, setParam] = useState({
    ...parameters,
    startDate: startDate,
    endDate: endDate,
  });

  const handleChangeProcedureType = (object: any) => {
    setParam({ ...param, procedureType: object });
  };

  const procedureTypeCode = procedureTypeOptions.find(
    (x) => x.type === procedureTypeParam,
  );

  const getProcedureStatus = (status: string) =>
    procedureTypeOptions.find(
      (x) => x.value.toUpperCase() === status?.toUpperCase(),
    );

  // Update the searchValue function to maintain current date range
  const searchValue = (val: any) => {
    val = val.trim();
    // encode value
    val = encodeURIComponent(val);
    // Maintain current date range in the URL
    // navigate(`?page=1&q=${val}&fd=${fd || ""}&ed=${ed || ""}`);
    navigate(`?page=1&q=${val}&type=${procedureTypeParam}`);
  };

  // Update the clear search functionality
  const handleClearSearch = () => {
    setValue(""); // Clear the input value
    searchValue(""); // Clear the search parameter
  };

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (curPage !== value) {
      navigate(
        `?page=${value}&q=${encodedQuery || ""}&type=${procedureTypeParam || ""}`,
      );
    }
  };

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
              whiteSpace: "nowrap",
            }}
            align="left"
          >
            Patient
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="left"
          >
            Contact Number
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="left"
          >
            Procedure
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="left"
          >
            Date
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="right"
          >
            Amount
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="left"
          >
            Status
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number,
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number],
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const getProcedureStatusList = async () => {
    try {
      let procedureTypes = param.procedureType
        ?.map((d: any) => d.value)
        .join(",");
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("userId", userContext?.loginDetail.userId?.toString());
      if (startDate)
        params.append("startDate", format(new Date(startDate), "yyyy-MM-dd"));
      if (endDate)
        params.append("endDate", format(new Date(endDate), "yyyy-MM-dd"));
      params.append("pageIndex", curPage?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn || "name");
      params.append("sortDirection", param.sortDirection || "ASC");
      params.append("searchValue", query || "");
      params.append("mode", "LIST");
      params.append("procedureTypes", procedureTypeCode?.value || "");

      const result: any = await GetProcedureStatusList(params);
      if (result?.data?.data?.procedureReportColl) {
        const resultData: ResultData = result?.data?.data;
        let procedureReportColl = resultData?.procedureReportColl || [];

        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (curPage - 1) * param.pageSize + 1;
        let endIndex =
          (curPage - 1) * param.pageSize + (procedureReportColl?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  async function openProcedureStatusReportPopup(type: any) {
    var attachmentName = "";
    if (type === "pdf") {
      attachmentName = "ProcedureStatus.pdf";
      // attachmentName =
      //   "Form3C/Form25" +
      //   format(new Date(fd), "_yyyyMMdd") +
      //   format(new Date(ed), "-yyyyMMdd") +
      //   ".pdf";
    } else {
      attachmentName = "ProcedureStatus.xls";
      // attachmentName =
      //   "Form3C/Form25" +
      //   format(new Date(fd), "_yyyyMMdd") +
      //   format(new Date(ed), "-yyyyMMdd") +
      //   ".xls";
    }
    let procedureTypes = param.procedureType
      ?.map((d: any) => d.value)
      .join(",");
    setIsLoading(true);
    let params = new URLSearchParams();
    params.append("reportName", "ProcedureStatusReport");
    params.append("reportType", type);
    params.append("consumerId", userContext.consumerId.toString());
    params.append("organizationId", userContext.organizationId.toString());
    params.append("branchId", userContext.branchId.toString());
    params.append("userId", userContext?.loginDetail.userId?.toString());
    if (param?.startDate) {
      params.append("startDate", format(param.startDate, "yyyy-MM-dd"));
    }
    if (param?.endDate) {
      params.append("endDate", format(param.endDate, "yyyy-MM-dd"));
    }
    params.append("pageIndex", param.pageIndex?.toString());
    params.append("pageSize", param.pageSize?.toString());
    params.append("sortColumn", param.sortColumn);
    params.append("sortDirection", param.sortDirection);
    params.append("searchValue", query || "");
    params.append("fileName", attachmentName);
    params.append("mode", "EXPORT");
    params.append("procedureTypes", procedureTypeCode?.value || "");
    try {
      const result: any = await GetProcedureStatusReport(params);
      if (result && result.data && result.data.data) {
        var fileContent = result?.data?.data?.fileData;
        var attachmentData = fileContent;
        ExportFilesFunction(attachmentName, type, attachmentData);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  }

  // Update the useEffect for param changes to properly handle both search and date
  useEffect(() => {
    setPage_Index(curPage);
    setParam({
      ...parameters,
      searchValue: query,
      pageIndex: curPage,
      startDate: startDate,
      endDate: endDate,
    });
  }, [query, curPage]);

  // FETCH DATA
  const fetchData = async () => {
    try {
      setIsLoading(true);
      await getProcedureStatusList();
    } catch (e) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [param, curPage, query]);

  return (
    <>
      <Box>
        <Box className="main-container">
          <Grid container className="page-header">
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", height: "100%", alignItems: "center" }}
              >
                <CustomBreadcrumbs />
              </Box>
            </Grid>
          </Grid>
          <Grid container className="list-container">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <SkeletonWrapper
                  isLoading={isLoading}
                  className="skeleton-search-box"
                >
                  <TextField
                    id="searchBox"
                    placeholder="Search..."
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        searchValue(value);
                      }
                    }}
                    type="text"
                    variant="outlined"
                    size="small"
                    onChange={(e) => setValue(e.target.value)}
                    value={value}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            size="small"
                            onClick={() => {
                              searchValue(value);
                            }}
                          >
                            {" "}
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: value && (
                        <IconButton
                          aria-label="clear search"
                          onClick={handleClearSearch}
                        >
                          <ClearIcon />
                        </IconButton>
                      ),
                    }}
                  />
                </SkeletonWrapper>
              </Grid>
              <Grid item xs={12} sm={3} md={3} lg={3}>
                {/* <SkeletonWrapper
                  isLoading={isLoading}
                  className="skeleton-search-box"
                >
                  <span
                    className="d-inline-block"
                    data-toggle="popover"
                    data-trigger="focus"
                    data-content="Please selecet doctor(s)"
                  >
                    <MultiSelect
                      placeholderLabel="Status"
                      options={procedureTypeOptions}
                      onChange={handleChangeProcedureType}
                      value={param.procedureType}
                      isSelectAll={true}
                    />
                  </span>
                </SkeletonWrapper> */}
              </Grid>
              {userContext.loginDetail.userRole !== "user" && (
                <Grid
                  item
                  xs={12}
                  sm={3}
                  md={3}
                  lg={2}
                  className="search-bar pdf-icon"
                  sx={{
                    ml: "auto",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      backgroundColor: "primary.main",
                      color: "white",
                      padding: "8px",
                      borderRadius: "5px",
                    }}
                  >
                    Total:{" "}
                    {(resultData?.totalReceivedAmount ?? 0).toLocaleString(
                      "en-IN",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </Typography>
                  {resultData?.procedureReportColl?.length > 0 && (
                    <>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-search-pdf-icon"
                      >
                        <img
                          src="/xls.png"
                          alt=""
                          className="pdf-icon-hover"
                          onClick={() => {
                            openProcedureStatusReportPopup("Excel");
                          }}
                        />
                      </SkeletonWrapper>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-search-pdf-icon"
                      >
                        <img
                          src="/pdf.png"
                          alt=""
                          className="pdf-icon-hover"
                          onClick={() => {
                            openProcedureStatusReportPopup("pdf");
                          }}
                        />
                      </SkeletonWrapper>
                    </>
                  )}
                </Grid>
              )}
            </Grid>

            <Grid item xs={12} mt={2}>
              <TableContainer
                sx={{
                  width: "100%",
                  overflowX: "auto",
                }}
              >
                <Table
                  sx={{
                    minWidth: 900,
                  }}
                >
                  <EnhancedTableHead
                    numSelected={0}
                    onSelectAllClick={() => {}}
                    order={order}
                    orderBy={orderBy}
                    rowCount={0}
                  />
                  <TableBody>
                    {isLoading ? (
                      Array(skeletonRowCount)
                        ?.fill(0)
                        ?.map((skeletonRow, index) => {
                          return (
                            <TableRow key={"skeleton-row" + index?.toString()}>
                              <TableCell colSpan={8}>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <span></span>
                                </SkeletonWrapper>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <>
                        {stableSort(
                          resultData?.procedureReportColl,
                          getComparator(order, orderBy),
                        ).map((item: any, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="left"
                              >
                                {item?.patientName}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="left"
                              >
                                {item?.primaryMobileNumber}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="left"
                              >
                                {item?.procedureName}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="left"
                              >
                                {item?.scheduledDate
                                  ? format(
                                      new Date(item.scheduledDate),
                                      "dd-MM-yyyy",
                                    )
                                  : ""}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  fontWeight: "bold",
                                }}
                                align="right"
                              >
                                {(item?.price).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="left"
                              >
                                <Box
                                  sx={{
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 1,
                                    backgroundColor: getProcedureStatus(
                                      item?.procedureStatus,
                                    )?.bg,
                                    maxWidth: "80px",
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "11px !important",
                                      color: "#FFFFFF",
                                    }}
                                  >
                                    {
                                      getProcedureStatus(item?.procedureStatus)
                                        ?.label
                                    }
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {resultData?.procedureReportColl?.length > 0 && (
                          <TableRow
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            <TableCell align="left" colSpan={4}>
                              <Typography sx={{ fontWeight: "bold" }}>
                                Page Total
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: "bold" }}>
                                {`${(
                                  resultData?.procedureReportColl?.reduce(
                                    (sum: number, item: any) =>
                                      sum + (parseFloat(item?.price) || 0),
                                    0,
                                  ) ?? 0
                                ).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                              </Typography>
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        )}
                        {resultData?.procedureReportColl?.length <= 0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={8}>
                              No record(s) found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} paddingTop={1}>
              {resultData?.procedureReportColl?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-label"
                    >
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-stack"
                    >
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData} // NUMBER OF PAGES
                          page={curPage} // CURRENT PAGE
                          onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default ProcedureStatusReport;
